/**
 * Mapping from Stripe Product IDs to Auth0 role names.
 *
 * Used for display purposes only — Auth0 is the source of truth for roles.
 */
/**
 * Pro product ID — read from env so the same code works against Stripe Test
 * Mode locally (`sk_test_…` + the test product ID) and Live Mode in prod
 * (`sk_live_…` + the live product ID). Stripe Test and Live are completely
 * separate object spaces, so the IDs always differ — there's no single
 * hardcodable constant that covers both modes.
 */
const PRO_PRODUCT_ID = process.env.NEXT_PUBLIC_STRIPE_PRO_PRODUCT_ID;

export const STRIPE_PRODUCT_TO_ROLE: Record<string, string> = PRO_PRODUCT_ID
  ? { [PRO_PRODUCT_ID]: 'Pro' }
  : {};

/**
 * Monthly Pro credit allowance. The webhook resets any leftover balance and
 * grants this amount on every `invoice.payment_succeeded` (first payment +
 * each renewal). For yearly plans it covers month zero; the credit-refill
 * cron resets and grants months 1..11 at clamped monthly UTC anchors within
 * that Stripe billing period.
 */
export const PRO_CREDITS_PER_PERIOD = 5000;

/** One-time credit grant for a brand-new (free) user at signup. */
export const FREE_SIGNUP_CREDITS = 10;

/**
 * 7-day Pro trial for genuinely new signups — no card, no credit grant (the
 * user keeps their 100 signup credits; Pro *features* are what the trial
 * unlocks). Created server-side at first session (lib/pro-trial.ts); Stripe
 * cancels it at trial end (`missing_payment_method: 'cancel'`) and the
 * subscription.deleted webhook drops the Pro role.
 *
 * The env switch is the kill switch: off by default so rollback is an env
 * flip + restart, not a deploy — same philosophy as
 * TRANSACTIONAL_EMAIL_ENABLED. In-flight trials still expire cleanly via
 * Stripe when it's off.
 */
export const PRO_TRIAL_DAYS = 7;
export const PRO_TRIAL_ENABLED =
  (process.env.PRO_TRIAL_ENABLED ?? '').toLowerCase() === 'true';

/**
 * Per-tier caps for the "Lists" feature. Free users cannot create lists at
 * all (the New List action upsells); these bound what a paid account can
 * grow to, keeping the share pages, hydration queries and the daily
 * watch-refresh scrape budget predictable. Enforced in the /api/lists
 * routes via lib/plan-limits.ts listLimits(); `null` means uncapped (the
 * backend still keeps a 10k sanity ceiling per list). Accounts that exceed
 * a cap (grandfathered Pro users from the 10-list era, downgrades) keep
 * their lists — caps apply on create/add only.
 */
export const PRO_LISTS_LIMIT = 3;
export const PRO_LIST_COMPANIES_LIMIT = 100;
export const ENTERPRISE_LISTS_LIMIT = 10;
export const ENTERPRISE_LIST_COMPANIES_LIMIT: number | null = null;

/**
 * Per-tier caps for saved searches.
 *
 * Unlike Lists, the free tier gets a real allowance rather than an upsell
 * wall: one saved search is enough to feel the value, and hitting the cap
 * offers "replace" beside "upgrade" so the feature never becomes a dead end.
 *
 * Enterprise is capped rather than uncapped because every card on the manage
 * page costs a COUNT over `mv_search` to refresh — 50 is a real step up from
 * Pro while keeping that page's worst case bounded. Enforced in the
 * /api/saved-searches routes via lib/plan-limits.ts savedSearchLimits();
 * the backend keeps a plan-blind 100 ceiling.
 */
export const FREE_SAVED_SEARCHES_LIMIT = 1;
export const PRO_SAVED_SEARCHES_LIMIT = 10;
export const ENTERPRISE_SAVED_SEARCHES_LIMIT = 50;

/**
 * Stripe Product ID → credits granted per billing period. Products absent
 * here fall back to {@link PRO_CREDITS_PER_PERIOD} in the webhook, so a
 * missing/wrong env var still grants the Pro allowance — better safe than
 * silently giving paying users 0 credits.
 */
export const STRIPE_PRODUCT_TO_CREDITS: Record<string, number> = PRO_PRODUCT_ID
  ? { [PRO_PRODUCT_ID]: PRO_CREDITS_PER_PERIOD }
  : {};

/**
 * Credit top-up: one checkout unit = 1 € net = 100 credits. Users pick the
 * quantity directly in Stripe Checkout (adjustable quantity); the webhook
 * grants `quantity × CREDIT_TOPUP_CREDITS_PER_UNIT` into the durable
 * purchased wallet (survives Pro period resets — see the backend ledger).
 */
export const CREDIT_TOPUP_CREDITS_PER_UNIT = 100;
/** Net price per top-up unit in euro cents (1 €). VAT is added at checkout. */
export const CREDIT_TOPUP_UNIT_AMOUNT_CENTS = 100;
/** Default quantity preselected in Stripe Checkout. */
export const CREDIT_TOPUP_DEFAULT_QUANTITY = 5;
/**
 * Quantity bounds enforced by Stripe Checkout's quantity picker and clamped
 * server-side in create-checkout-session: a top-up is at least 5 € (500
 * credits) — smaller one-off card payments cost more in fees than they bring.
 */
export const CREDIT_TOPUP_MIN_QUANTITY = 5;
export const CREDIT_TOPUP_MAX_QUANTITY = 500;
/**
 * Stable Stripe price lookup_key used to find (or lazily create) the top-up
 * price in whichever mode (test/live) the server runs in — same pattern as
 * the VAT tax rate, no dashboard setup or per-mode env var required.
 */
export const CREDIT_TOPUP_PRICE_LOOKUP_KEY = 'credit_topup_100_per_eur';
